import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SelectedFilters, SortOption } from '../../interface/interface';
import {
  GuestCartProvider,
  useGuestCart,
} from '../../context/guestCartContext';
import AllProductsFilters from '../../components/AllProductsFilters';
import {
  filterProductsBySelection,
  readSelectedCategories,
} from '../../utils/productCategoryTree';
import Select, { SingleValue } from 'react-select';
import NoProductsFound from '../../components/NoProductsFound';
import GuestProductCards from '../../components/guest/GuestProductCards';
import { GuestUserContext } from '../../context/guestContext';
import {
  customStyles,
  EMPTY_FILTERS,
  SORT_OPTIONS,
} from '../../constant/constant';

function readInitialFilters(): SelectedFilters {
  if (typeof window === 'undefined') return EMPTY_FILTERS;
  const storedCategories = readSelectedCategories();
  if (!storedCategories) return EMPTY_FILTERS;
  return { ...EMPTY_FILTERS, ...storedCategories };
}

export default function GuestNonScriptRange() {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<SelectedFilters>(readInitialFilters);
  const [sortOption, setSortOption] = useState<SortOption | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const primaryCategory = (location.state as { primaryCategory?: string })
      ?.primaryCategory;
    if (!primaryCategory) return;
    setFilters({ ...EMPTY_FILTERS, primaryCategories: [primaryCategory] });
  }, [location.key]);

  const handleFilterChange = (selected: SelectedFilters) => {
    setFilters(selected);
    if ((location.state as { primaryCategory?: string })?.primaryCategory) {
      navigate(location.pathname, { replace: true, state: null });
    }
  };
  const { guestSearchProducts, loading: isProductsLoading } =
    useContext(GuestUserContext);
  const { addToCartMutation, removeProductFromCartMutation } = useGuestCart();

  const handleSortChange = (option: SingleValue<SortOption>) => {
    setSortOption(option);
  };

  useEffect(() => {
    if (
      !loading ||
      !addToCartMutation.isPending ||
      !removeProductFromCartMutation.isPending
    ) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [
    loading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  const filteredProducts = useMemo(
    () => filterProductsBySelection(guestSearchProducts, filters),
    [guestSearchProducts, filters],
  );

  const sortedProducts = useMemo(() => {
    if (!sortOption) return filteredProducts;

    const withParsedPrice = filteredProducts.map((p) => ({
      product: p,
      price: Number.parseFloat(p.price) || 0,
    }));

    withParsedPrice.sort((a, b) =>
      sortOption.value === 'low-to-high'
        ? a.price - b.price
        : b.price - a.price,
    );

    return withParsedPrice.map((entry) => entry.product);
  }, [filteredProducts, sortOption]);

  return (
    <div className="container-fullSpan">
      <GuestCartProvider>
        <p className="text-sm text-gray-500 block xl:hidden mb-2">
          {sortedProducts.length} product
          {sortedProducts.length !== 1 ? 's' : ''}
        </p>

        <div className="grid grid-cols-12">
          <div className="hidden xl:block col-span-3 me-5">
            <AllProductsFilters
              onFilterChange={handleFilterChange}
              allProducts={guestSearchProducts}
              selectedFilters={filters}
            />
          </div>

          <div className="xl:col-span-9 col-span-12">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex-1 xl:flex-none block xl:hidden">
                  <AllProductsFilters
                    onFilterChange={handleFilterChange}
                    allProducts={guestSearchProducts}
                    selectedFilters={filters}
                  />
                </div>

                <div className="xl:block hidden">
                  <p className="text-sm text-gray-500">
                    {sortedProducts.length} product
                    {sortedProducts.length !== 1 ? 's' : ''}
                  </p>
                </div>

                <Select
                  placeholder="Sort by"
                  options={SORT_OPTIONS}
                  isLoading={false}
                  value={sortOption}
                  onChange={handleSortChange}
                  isClearable
                  className="flex-1 xl:flex-none rounded-md w-full md:w-44 text-xs text-primary-blue-500"
                  isSearchable={false}
                  styles={customStyles}
                />
              </div>

              {isProductsLoading ? (
                <p className="text-sm text-gray-400">Loading products...</p>
              ) : sortedProducts.length === 0 ? (
                <NoProductsFound
                  filters={filters}
                  onClearFilters={() => handleFilterChange(EMPTY_FILTERS)}
                />
              ) : (
                <ul className="grid grid-cols-2 gap-4 sm-land:grid-cols-3 md-large:grid-cols-3 xl:grid-cols-4">
                  {sortedProducts.map((product) => (
                    <GuestProductCards
                      key={product.id}
                      isGuest={true}
                      product={product}
                      addProductToCart={async () => {
                        await addToCartMutation.mutateAsync(
                          product.product_code,
                        );
                      }}
                      removeProductFromCart={async () => {
                        await removeProductFromCartMutation.mutateAsync(
                          product.product_code,
                        );
                      }}
                      isAccessories={false}
                      allowedProduct={false}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </GuestCartProvider>
    </div>
  );
}
